// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import getFocalPointStyles from "@toolkit/Modules/mobius/utilities/getFocalPointStyles";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const Banner = getWidgetComponent("Basic/Banner");

const enum fields {
    backgroundColor = "backgroundColor",
    focalPoint = "focalPoint",
    hasMobileSettings = "hasMobileSettings",
    image = "image",
    invertColor = "invertColor",
    mobileFocalPoint = "mobileFocalPoint",
    mobileImage = "mobileImage",
    overlayColor = "overlayColor",
    showOverlay = "showOverlay",
    subheading = "subheading",
    surtitle = "surtitle",
    title = "title",
}

type FocalPoint = Parameters<typeof getFocalPointStyles>[0];

interface BlogBannerProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.focalPoint]: FocalPoint;
        [fields.hasMobileSettings]: boolean;
        [fields.image]: string;
        [fields.invertColor]: boolean;
        [fields.mobileFocalPoint]: string;
        [fields.mobileImage]: string;
        [fields.overlayColor]: string;
        [fields.showOverlay]: boolean;
        [fields.subheading]: string;
        [fields.surtitle]: string;
        [fields.title]: string;
    };
}

export interface BlogBannerStyles {
    wrapper?: InjectableCss;
    surtitle?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    subheading?: InjectableCss;
}

export const blogBannerStyles: BlogBannerStyles = {
    wrapper: {
        css: css`
            text-align: center;
        `,
    },
    surtitle: {
        css: css`
            display: block;

            ${({ theme }) => css`
                ${breakpointValues(
                    [
                        theme.design.typography.size.large,
                        null,
                        theme.design.typography.size.larger,
                        null,
                        theme.design.typography.size.largest,
                    ],
                    "font-size",
                )}
                line-height: ${theme.design.typography.lineHeight.base};
            `}
        `,
    },
    title: {
        variant: "h1",
        css: css`
            ${spacingStyles(0, pxToRem(16))}
        `,
    },
    subheading: {
        css: css`
            ${({ theme }) => css`
                ${breakpointValues(
                    [
                        theme.design.typography.size.base,
                        null,
                        theme.design.typography.size.large,
                        null,
                        theme.design.typography.size.larger,
                    ],
                    "font-size",
                )}
                line-height: ${theme.design.typography.lineHeight.base};
            `}
        `,
    },
};

const styles = blogBannerStyles;

const BlogBanner: FC<BlogBannerProps> = ({ fields }) => {
    const page = useCurrentPage();

    if (!page) {
        return null;
    }

    const {
        fields: { title: pageTitle },
    } = page;

    const {
        backgroundColor,
        subheading,
        focalPoint,
        hasMobileSettings,
        image,
        invertColor,
        mobileFocalPoint,
        mobileImage,
        overlayColor,
        showOverlay,
        surtitle,
        title,
    } = fields;

    if (!image && !title && !pageTitle) {
        return null;
    }

    return (
        <Banner
            content={
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.title}>
                        {surtitle ? <Typography {...styles.surtitle}>{surtitle}</Typography> : null}
                        {title || pageTitle}
                    </Typography>
                    {subheading ? (
                        <StyledWrapper {...styles.subheading}>{parse(subheading, parserOptions)}</StyledWrapper>
                    ) : null}
                </StyledWrapper>
            }
            fields={{
                backgroundColor,
                focalPoint,
                hasMobileSettings,
                image,
                invertColor,
                mobileImage,
                mobileFocalPoint,
                overlayColor,
                showOverlay,
                variant: "blog",
            }}
        />
    );
};

const overlayTab = { displayName: "Overlay", sortOrder: 1 };
const mobileTab = { displayName: "Mobile", sortOrder: 2 };

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: BlogBanner,
    definition: {
        group: constants.widgetGroup.blog,
        allowedContexts: [constants.pageType.blogHomePage, constants.pageType.blogCategoryPage],
        displayName: "Banner",
        fieldDefinitions: [
            {
                name: fields.invertColor,
                displayName: "Invert Text Color",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
            },
            {
                name: fields.image,
                defaultValue: "",
                editorTemplate: "ImagePickerField",
                fieldType: "Contextual",
                tooltip: "If empty, the banner will not be displayed.",
                tooltipPosition: "left",
            },
            {
                name: fields.focalPoint,
                displayName: "Image Focal Point",
                editorTemplate: "DropDownField",
                fieldType: "Contextual",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "CustomColorPickerField",
                fieldType: "Contextual",
                defaultValue: "",
            },
            {
                name: fields.surtitle,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                tooltip: "Text that will be shown in the banner, on top of the title.",
                tooltipPosition: "left",
            },
            {
                name: fields.title,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                tooltip: "Title that will be shown in the banner. If empty, the page title will be used.",
                tooltipPosition: "left",
            },
            {
                name: fields.subheading,
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "Contextual",
                tooltip: "Short text that will be shown in the banner under the title.",
                tooltipPosition: "left",
            },
            {
                name: fields.showOverlay,
                displayName: "Show Overlay",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                variant: "toggle",
                tab: overlayTab,
            },
            {
                name: fields.overlayColor,
                displayName: "Overlay Color",
                editorTemplate: "CustomColorPickerField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: overlayTab,
                isVisible: x => x.fields.showOverlay,
            },
            {
                name: fields.hasMobileSettings,
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                variant: "toggle",
                tab: mobileTab,
            },
            {
                name: fields.mobileImage,
                defaultValue: "",
                editorTemplate: "ImagePickerField",
                fieldType: "Contextual",
                tab: mobileTab,
                isVisible: x => x.fields.hasMobileSettings,
            },
            {
                name: fields.mobileFocalPoint,
                displayName: "Image Focal Point",
                editorTemplate: "DropDownField",
                fieldType: "Contextual",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                tab: mobileTab,
                isVisible: x => x.fields.hasMobileSettings,
            },
        ],
    },
};

export default widgetModule;
